import { assign, fromPromise, setup } from 'xstate';
import { z } from 'zod';
import type { DraftSubmitResult } from '~/features/market/negotiations/types';
import type { NegotiationDraftApiResponse } from '~/features/market/negotiations/utils';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData, postData } from '~/utils/service';
import { isUncertainListingActionError } from '../listing-search/errors';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '../requestPolicy';
import { B2BOpportunitySchema } from './schemas';

export type B2BApplySource = { responseId: number; listingId: number; title: string };
type Context = {
  source: B2BApplySource | null;
  draft: NegotiationDraftApiResponse | null;
  open: boolean;
  uncertain: boolean;
  error: string | null;
  result: { responseId: number } | null;
};

export const b2bInquiryApplyMachine = setup({
  types: {
    context: {} as Context,
    events: {} as
      | { type: 'OPEN'; source: B2BApplySource }
      | { type: 'CLOSE' }
      | { type: 'CONFIRM' }
      | { type: 'RELOAD_DRAFT' }
      | { type: 'RETRY' }
      | { type: 'REFRESH' }
      | { type: 'DISCARD_RETRY' },
  },
  actors: {
    loadDraft: fromPromise(
      ({ input, signal }: { input: B2BApplySource | null; signal: AbortSignal }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (!input) throw new Error('No inquiry selected.');
            const draft = await getData<NegotiationDraftApiResponse>(
              'jobResponseNegotiationDraft',
              {
                params: { listingType: 'MSAAS', responseId: input.responseId.toString() },
                signal: requestSignal,
              }
            );
            if (
              draft.listingType !== 'MSAAS' ||
              draft.responseType !== 'msaas' ||
              draft.jobResponseId !== input.responseId ||
              draft.listingId !== input.listingId ||
              draft.senderRole !== 'RESPONDER' ||
              !draft.terms ||
              typeof draft.terms !== 'object' ||
              Array.isArray(draft.terms)
            ) {
              throw new Error('Listing terms could not be verified for this inquiry.');
            }
            return structuredClone(draft);
          },
          MARKET_READ_TIMEOUT_MS,
          signal
        )
    ),
    apply: fromPromise(
      ({ input, signal }: { input: Pick<Context, 'source' | 'draft'>; signal: AbortSignal }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (!input.source || !input.draft) throw new Error('No confirmed listing terms.');
            const result = await postData<DraftSubmitResult>('jobResponseInitialNegotiation', {
              pathParams: { responseId: input.source.responseId.toString() },
              data: { negotiationTerms: structuredClone(input.draft.terms) },
              signal: requestSignal,
            });
            if (
              result.jobResponseId !== input.source.responseId ||
              result.responseType !== 'msaas' ||
              result.jobResponse.id !== input.source.responseId ||
              result.jobResponse.listingType !== 'MSAAS' ||
              result.jobResponse.msaasListingId !== input.source.listingId ||
              !Number.isSafeInteger(result.negotiationId) ||
              result.negotiationId < 1
            ) {
              throw new Error('Application acknowledgement could not be verified.');
            }
            return { responseId: result.jobResponseId };
          },
          MARKET_ACTION_TIMEOUT_MS,
          signal
        )
    ),
    recover: fromPromise(
      ({ input, signal }: { input: B2BApplySource | null; signal: AbortSignal }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (!input) throw new Error('No inquiry selected.');
            const rows = z.array(B2BOpportunitySchema).parse(
              await getData('b2bJobOpportunities', {
                // [MCV-1850] A declined inquiry can be re-applied to, and the default list hides it.
                params: { status: 'all', includeDeclined: 'true' },
                signal: requestSignal,
              })
            );
            const current = rows.find(
              (row) => row.responseId === input.responseId && row.listing.id === input.listingId
            );
            return current?.latestNegotiation && current.latestNegotiation.version > 0
              ? { responseId: current.responseId }
              : null;
          },
          MARKET_READ_TIMEOUT_MS,
          signal
        )
    ),
  },
}).createMachine({
  id: 'b2bInquiryApply',
  initial: 'idle',
  context: { source: null, draft: null, open: false, uncertain: false, error: null, result: null },
  on: {
    CLOSE: { actions: assign({ open: false }) },
    OPEN: {
      target: '.loading',
      reenter: true,
      actions: assign({ source: ({ event }) => ({ ...event.source }), open: true, result: null }),
    },
  },
  states: {
    idle: {},
    loading: {
      entry: assign({ draft: null, error: null }),
      invoke: {
        src: 'loadDraft',
        input: ({ context }) => context.source,
        onDone: { target: 'ready', actions: assign({ draft: ({ event }) => event.output }) },
        onError: {
          target: 'unavailable',
          actions: assign({
            error:
              'Unable to load current listing terms. Refresh the inquiry or reload the terms before applying.',
          }),
        },
      },
    },
    ready: {
      on: {
        CONFIRM: {
          guard: ({ context }) => context.open && context.draft !== null,
          target: 'sending',
        },
      },
    },
    unavailable: { on: { RELOAD_DRAFT: 'loading' } },
    sending: {
      entry: assign({ error: null, result: null }),
      on: { CLOSE: {}, OPEN: {} },
      invoke: {
        src: 'apply',
        input: ({ context }) => ({ source: context.source, draft: context.draft }),
        onDone: {
          target: 'idle',
          actions: assign({ result: ({ event }) => event.output, open: false, uncertain: false }),
        },
        onError: {
          target: 'recovering',
          actions: assign({
            uncertain: ({ context, event }) =>
              context.uncertain || isUncertainListingActionError(event.error),
            error: 'Application not confirmed. Checking the inquiry before another action.',
          }),
        },
      },
    },
    recovering: {
      on: { CLOSE: {}, OPEN: {} },
      invoke: {
        src: 'recover',
        input: ({ context }) => context.source,
        onDone: [
          {
            guard: ({ event }) => event.output !== null,
            target: 'idle',
            actions: assign({
              result: ({ event }) => event.output,
              open: false,
              uncertain: false,
              error: null,
            }),
          },
          { guard: ({ context }) => context.uncertain, target: 'uncertain' },
          {
            target: 'unavailable',
            actions: assign({
              error:
                'The offer was not started. Reload current listing terms before applying again.',
            }),
          },
        ],
        onError: 'uncertain',
      },
    },
    uncertain: {
      entry: assign({
        uncertain: true,
        error: 'We could not confirm whether the offer started. No retry happens automatically.',
      }),
      on: {
        OPEN: {
          actions: assign({
            open: ({ context, event }) =>
              event.source.responseId === context.source?.responseId &&
              event.source.listingId === context.source?.listingId,
          }),
        },
        RETRY: { guard: ({ context }) => context.open, target: 'sending' },
        REFRESH: 'recovering',
        DISCARD_RETRY: {
          target: 'idle',
          actions: assign({ open: false, uncertain: false, error: null, result: null }),
        },
      },
    },
  },
});
